import * as R from 'remeda';
import { max as dateMax, min as dateMin, format } from 'date-fns';
import { AutomationTarget } from '@rossum/api-client/queues';
import { alpha } from '@rossum/ui/material';
import { ChartsYAxisProps } from '@rossum/ui/x-charts-pro';
import { Prettify } from '../../../../../../lib/typeUtils';
import { AggregationPeriod, TransformedAutomationInsights } from '../../types';
import { getEndOfPeriod } from '../../utils';
import { PrecisionRateChartData } from './types';

export const CHART_HEIGHT = 160;

export const dateFormatter = ({
  aggregationPeriod,
  value,
  locale,
  isTooltip,
  startDate,
  endDate,
}: {
  value: string;
  aggregationPeriod: AggregationPeriod;
  locale?: Locale;
  isTooltip: boolean;
  startDate: string | null;
  endDate: string | null;
}) => {
  try {
    const referenceDate = new Date(value);

    if (aggregationPeriod === 'days') {
      return format(referenceDate, 'd MMM');
    }

    if (!isTooltip) {
      return format(
        referenceDate,
        aggregationPeriod === 'months' ? 'MMM' : 'd MMM'
      );
    }

    const endOfPeriod = getEndOfPeriod({
      referenceDate,
      aggregationPeriod,
      locale,
    });

    const clampedStart = startDate
      ? dateMax([referenceDate, new Date(startDate)])
      : referenceDate;

    const clampedEnd = endDate
      ? dateMin([endOfPeriod, new Date(endDate)])
      : endOfPeriod;

    const start = format(clampedStart, 'd', { locale });
    const end = format(clampedEnd, 'd MMM', { locale });

    return `${start} - ${end}`;
  } catch (e) {
    // oxlint-disable-next-line
    console.error(e);
    return '';
  }
};

export const getCommonYAxisProps = (
  textColor: string,
  { min } = { min: 0 }
): ChartsYAxisProps => ({
  tickLabelStyle: { fill: textColor },
  disableLine: true,
  disableTicks: true,
  tickNumber: 3,
  tickLabelInterval: (value: number) => value !== min,
  tickLabelPlacement: 'tick',
});

export const getCommonGradientAreaYAxisProps = (
  color: string,
  { min, max } = { min: 0, max: 100 }
) =>
  ({
    colorMap: {
      type: 'continuous',
      min,
      max,
      color: ['transparent', alpha(color, 0.3)],
    },
  }) as const;

export const getAutomationRateChartDataset = (
  data: TransformedAutomationInsights['aggregatedDocumentAutomationTimeseries']
) => {
  const result = R.map(
    data,
    ({
      automatedCount,
      nonAutomatedCount,
      touchedCount,
      touchlessCount,
      date,
    }) => {
      const totalDocumentCount = automatedCount + nonAutomatedCount;
      const automationRate =
        totalDocumentCount !== 0 ? automatedCount / totalDocumentCount : 0;
      const totalTouchlessCount = touchedCount + touchlessCount;
      const touchlessRate =
        totalTouchlessCount !== 0 ? touchlessCount / totalTouchlessCount : 0;

      return {
        automationRate,
        automatedCount,
        nonAutomatedCount,
        touchedCount,
        touchlessCount,
        touchlessRate,
        date,
      };
    }
  );

  return {
    automatedCount: R.map(result, item => item.automatedCount),
    nonAutomatedCount: R.map(result, item => item.nonAutomatedCount),
    automationRate: R.map(result, item =>
      R.floor(item.automationRate * 100, 0)
    ),
    touchedCount: R.map(result, item => item.touchedCount),
    touchlessCount: R.map(result, item => item.touchlessCount),
    touchlessRate: R.map(result, item => R.floor(item.touchlessRate * 100, 0)),
    date: R.map(result, item => item.date),
  };
};

export const getAutomationBlockersChartData = (
  data: TransformedAutomationInsights['documentBlockers']
) => {
  return R.pipe(
    data,
    R.groupBy(item => item.blocker),
    R.mapValues(value => R.sumBy(value, item => item.documentCount)),
    R.entries(),
    R.sortBy(([_, value]) => -value),
    sortedPairs => ({
      labels: R.map(sortedPairs, ([key]) => key),
      values: R.map(sortedPairs, ([, value]) => value),
    })
  );
};

type GetPrecisionRateChartDatasetParams = Prettify<
  Pick<
    TransformedAutomationInsights,
    'aggregatedEstimatedErrorRateTimeseries'
  > & {
    savedAutomationTargets: AutomationTarget[] | undefined;
  }
>;

/**
 * A util fn to handle the edge case where Frontend receives invalid date values from Backend
 */
export const filterOutInvalidDates = <T extends { date: string }>(list: T[]) =>
  list.filter(value => !Number.isNaN(new Date(value.date).getTime()));

const normalizeDate = (date: string) => {
  const parsed = new Date(date);
  parsed.setUTCHours(0, 0, 0, 0);
  return parsed.toISOString();
};

export const getPrecisionRateChartDataset = ({
  aggregatedEstimatedErrorRateTimeseries,
  savedAutomationTargets,
}: GetPrecisionRateChartDatasetParams): PrecisionRateChartData[] => {
  const timeseriesDates = R.pipe(
    aggregatedEstimatedErrorRateTimeseries,
    R.map(item => normalizeDate(item.date)),
    R.sortBy(R.identity())
  );

  const timeseriesByDate = R.mapToObj(
    aggregatedEstimatedErrorRateTimeseries,
    item => [normalizeDate(item.date), item]
  );

  // Map a target date to the timeseries period it belongs to.
  // This handles weekly/monthly aggregated timeseries where a target set
  // mid-period should be assigned to the period's start date.
  const findTimeseriesPeriod = (targetDate: string): string => {
    const normalized = normalizeDate(targetDate);
    return R.findLast(timeseriesDates, d => d <= normalized) ?? normalized;
  };

  // Sorted target dates with their average automation target in a given period
  const sortedTargets = R.pipe(
    savedAutomationTargets ?? [],
    R.filter(target => target.type === 'automation_assistant_v1'),
    R.groupBy(item => findTimeseriesPeriod(item.datetime)),
    R.mapValues(targets => R.mean(targets.map(t => t.errorRateTarget))),
    R.entries(),
    R.sortBy(([date]) => date)
  );

  return timeseriesDates.reduce<PrecisionRateChartData[]>((acc, date) => {
    const timeseries = timeseriesByDate[date];

    const dateTime = !Number.isNaN(new Date(date).getTime())
      ? new Date(date).getTime()
      : null;

    if (!timeseries || !dateTime) return acc;

    // Find the latest target on or before this timeseries date
    const matchingTarget = R.findLast(
      sortedTargets,
      ([targetDate]) => targetDate <= date
    );

    const lastTarget = R.last(acc)?.precisionTarget ?? 0;
    const targetValue = matchingTarget?.[1]
      ? 1 - matchingTarget[1]
      : lastTarget;

    acc.push({
      date: dateTime,
      precision: {
        x: dateTime,
        // no estimate for this period -> keep the date on the axis, plot no point
        y:
          timeseries.errorRateEstimate === null
            ? null
            : 1 - timeseries.errorRateEstimate,
        isQualityEstimate: timeseries.isQualityEstimate,
      },
      precisionTarget: targetValue,
    });

    return acc;
  }, []);
};
